import { Segment } from '@mairistem/composants';
import { Liste, ListeTable } from '@mairistem/liste';

import dayjs from 'dayjs';
import _ from 'lodash';
import React, { type ReactNode } from 'react';
import { toast } from 'react-toastify';

import { CycleContext, FactureContext, LoadingContext, RegieContext } from '@/contexts';
import { useFacturesNonEncaissable } from '@/hooks';
import type { FactureListeItemType } from '@/types/facture';
import { downloadFacturePdf, formatFacturesToListeItem, utils_liste } from '@/utils';
import { useRegieStore } from '@/zustand';
import FacturesListeTableHeader from './FacturesListeTableHeader';
import FacturesListeTableHeaderGlobale from './FacturesListeTableHeaderGlobale';
import FacturesListeTableRow from './FacturesListeTableRow';
import FacturesListeTableRowGlobale from './FacturesListeTableRowGlobale';

interface FacturesListeProps {
  /** Si la liste est intégré dans une page ou si elle est seule. */
  standAlone?: boolean;
  /** Action d'une ligne */
  onEncaissement: (identifiantFacture: number) => void;
}

const FacturesListe = (props: FacturesListeProps) => {
  const { standAlone = false, onEncaissement } = props;

  // #region Declaration

  const { regies, selectedRegie } = React.useContext(RegieContext);

  const regie = React.useMemo(
    () => _.find(regies, { identifiant: selectedRegie }),
    [selectedRegie, regies],
  );

  const {
    total,
    factures,
    search,
    filters,
    setFactures,
    setFilters,
    setSorts,
    setSearch,
    setPage,
    applyDefaultFilters,
  } = React.useContext(FactureContext);

  const { cycles } = React.useContext(CycleContext);

  const {
    facturesLoadings: { factures: facturesLoading },
  } = React.useContext(LoadingContext);

  const encaissementPossible = useRegieStore((state) => state.regie?.encaissementPossible);

  const staledFactures = useFacturesNonEncaissable(factures);

  const formatedFactures = React.useMemo<FactureListeItemType[]>(
    () => formatFacturesToListeItem(factures, regie),
    [factures, regie],
  );

  const handleSort = React.useCallback(
    (sort) => {
      setPage(1);
      setSorts(
        _.isEmpty(sort)
          ? // tri par défaut
            null
          : _.mapValues(sort, (val) => (val === 'ascending' ? 'asc' : 'desc')),
      );
      setFactures([]);
    },
    [setFactures, setPage, setSorts],
  );

  const handleSearch = React.useCallback(
    async (value: string) => {
      setPage(1);
      setSearch(value);
      toast.dismiss();
    },
    [setPage, setSearch],
  );

  const handleFilter = React.useCallback(
    (f) => {
      // prevent la liste de trigger une deuxième fois l'appel à l'API
      // Si on essaye de réappliquer les filtres par défaut à tord
      if (utils_liste.areListeFiltersEqual(filters, f)) {
        return;
      }

      setPage(1);

      if (_.isEmpty(f)) {
        applyDefaultFilters();
      } else {
        setFilters(
          _.map(f, (filter) => {
            if (filter.type === 'date') {
              return {
                ...filter,
                value: dayjs(filter.value).format('YYYY-MM-DD'),
              };
            }

            return filter;
          }),
        );
      }
    },
    [filters, setPage, applyDefaultFilters, setFilters],
  );

  const handleLoad = React.useCallback(
    ({ page }) => {
      setPage(page);
    },
    [setPage],
  );

  const renderRow = (item, __, rowProps) => (
    <FacturesListeTableRow
      staled={_.includes(_.map(staledFactures, 'identifiant'), item.identifiant)}
      encaissementBloque={encaissementPossible === false}
      item={item}
      rowProps={rowProps}
      onOpenPdf={downloadFacturePdf}
      onEncaissement={onEncaissement}
    />
  );

  const renderRowGlobale = (item, __, rowProps) => (
    <FacturesListeTableRowGlobale
      item={item}
      rowProps={rowProps}
      onOpenPdf={downloadFacturePdf}
      onEncaissement={onEncaissement}
    />
  );

  // #endregion

  return (
    <Segment basic className="facture-liste" style={{ margin: 0, paddingTop: 0, height: '100%' }}>
      <Liste
        title=""
        onSearch={handleSearch}
        loading={facturesLoading}
        searchable
        search={{
          value: search,
          placeholder: standAlone
            ? 'Rechercher un n° de facture, un nom ou un montant de facture'
            : 'Rechercher un n° de facture, un nom ou un montant',
          clearable: true,
          autoFocus: true,
        }}
      >
        <Liste.Header> </Liste.Header>
        <Liste.Content>
          <div style={{ height: standAlone ? 'calc(100% - 100px)' : 450 }}>
            <ListeTable
              count={_.isEmpty(formatedFactures) ? 0 : total}
              {...(standAlone && {
                showCount: true,
                countLabel: total > 1 ? 'factures' : 'facture',
              })}
              items={formatedFactures}
              loading={facturesLoading}
              onFilter={handleFilter}
              onSort={handleSort}
              perPage={30}
              onLoad={handleLoad}
            >
              <ListeTable.Header>
                {standAlone ? (
                  <FacturesListeTableHeaderGlobale cycles={cycles} />
                ) : (
                  <FacturesListeTableHeader cycles={cycles} />
                )}
              </ListeTable.Header>
              {standAlone
                ? (renderRowGlobale as unknown as ReactNode)
                : (renderRow as unknown as ReactNode)}
            </ListeTable>
          </div>
        </Liste.Content>
      </Liste>
    </Segment>
  );
};

export default FacturesListe;
